<template>
  <div class="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900/70 via-black/50 to-gray-900/70">
    <div class="max-w-7xl mx-auto">
      <div class="mb-8 bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/40 p-6 relative">
        <div class="absolute inset-0 opacity-10 bg-amber-300 pattern-warhammer"></div>
        <div class="flex justify-between items-center relative">
          <h1 class="text-3xl font-bold text-amber-300 flex items-center">
            <i class="fas fa-gamepad mr-3 text-amber-400"></i> Gestion des Matchs
          </h1>
          <button 
            @click="showAddMatchModal = true"
            class="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-5 py-2.5 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 font-medium shadow-lg shadow-amber-900/20 hover:-translate-y-0.5 flex items-center"
          >
            <i class="fas fa-plus-circle mr-2"></i>
            Ajouter un match
          </button>
        </div>
      </div>

      <!-- Message d'erreur -->
      <div v-if="error" class="mb-4 p-4 bg-red-900/30 border border-red-700/30 rounded-lg text-red-300 flex items-center">
        <i class="fas fa-exclamation-triangle mr-2"></i> {{ error }}
      </div>

      <!-- Indicateur de chargement -->
      <div v-if="isLoading" class="mb-4 p-4 bg-amber-900/30 border border-amber-700/30 rounded-lg text-amber-300 flex items-center">
        <i class="fas fa-spinner fa-spin mr-2"></i> Chargement des matchs...
      </div>

      <!-- Formulaire d'ajout de match (Modal) -->
      <div v-if="showAddMatchModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 w-full max-w-md border border-amber-700/30 shadow-2xl shadow-amber-900/20 animate-fadeIn">
          <h2 class="text-2xl font-bold mb-6 text-amber-300 flex items-center">
            <i class="fas fa-bolt mr-2 text-amber-400"></i>
            {{ isEditing ? 'Modifier le match' : 'Nouveau Match' }}
          </h2>
          <form @submit.prevent="addMatch">
            <div class="mb-4">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-user mr-2 text-amber-400"></i> Joueur 1
              </label>
              <select 
                v-model="newMatch.player1" 
                class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                :disabled="isLoading || store.getters['user/isLoading']"
              >
                <option value="0" disabled>Sélectionnez un joueur</option>
                <option v-if="store.getters['user/isLoading']" value="" disabled>Chargement des joueurs...</option>
                <option v-if="hasLoadError" value="" disabled>Erreur: {{ loadErrorMessage }}</option>
                <option v-for="player in players" :key="player.id" :value="player.id">
                  {{ player.username }}
                </option>
              </select>
              <div v-if="!store.getters['user/isLoading'] && players.length === 0" class="mt-2">
                <p class="text-red-400 text-xs">Aucun joueur disponible.</p>
                <button 
                  @click="loadUsers" 
                  class="mt-1 text-xs text-amber-400 underline hover:text-amber-300 flex items-center"
                >
                  <i class="fas fa-sync-alt mr-1"></i> Recharger la liste
                </button>
              </div>
            </div>
            <div class="mb-4">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-shield-alt mr-2 text-amber-400"></i> Armée du Joueur 1
              </label>
              <select v-model="newMatch.army1" class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100">
                <option value="0" disabled>Sélectionnez une armée</option>
                <optgroup v-for="faction in ['Ordre', 'Chaos', 'Mort', 'Destruction']" :key="faction" :label="faction">
                  <option v-for="army in armies.filter(a => a.faction === faction)" :key="army.id" :value="army.id">
                    {{ army.name }}
                  </option>
                </optgroup>
              </select>
            </div>
            <div class="mb-4">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-user mr-2 text-amber-400"></i> Joueur 2
              </label>
              <select 
                v-model="newMatch.player2" 
                class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
                :disabled="isLoading || store.getters['user/isLoading']"
              >
                <option value="0" disabled>Sélectionnez un joueur</option>
                <option v-if="store.getters['user/isLoading']" value="" disabled>Chargement des joueurs...</option>
                <option v-if="hasLoadError" value="" disabled>Erreur: {{ loadErrorMessage }}</option>
                <option v-for="player in players" :key="player.id" :value="player.id">
                  {{ player.username }}
                </option>
              </select>
            </div>
            <div class="mb-4">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-shield-alt mr-2 text-amber-400"></i> Armée du Joueur 2
              </label>
              <select v-model="newMatch.army2" class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100">
                <option value="0" disabled>Sélectionnez une armée</option>
                <optgroup v-for="faction in ['Ordre', 'Chaos', 'Mort', 'Destruction']" :key="faction" :label="faction">
                  <option v-for="army in armies.filter(a => a.faction === faction)" :key="army.id" :value="army.id">
                    {{ army.name }}
                  </option>
                </optgroup>
              </select>
            </div>
            <div class="mb-4">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-trophy mr-2 text-yellow-400"></i> Type de match
              </label>
              <select v-model="newMatch.matchType" class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100">
                <option value="0" disabled>Sélectionnez le type de match</option>
                <option value="LEAGUE">League</option>
                <option value="LADDER">Ladder</option>
              </select>
            </div>
            <div class="mb-6">
              <label class="block text-amber-200 mb-2 font-medium flex items-center">
                <i class="fas fa-crown mr-2 text-yellow-400"></i> Vainqueur
              </label>
              <select v-model="newMatch.winner" class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100">
                <option value="0" disabled>Sélectionnez le vainqueur</option>
                <option :value="newMatch.player1">{{ getPlayerName(newMatch.player1) }}</option>
                <option :value="newMatch.player2">{{ getPlayerName(newMatch.player2) }}</option>
              </select>
            </div>
            <div class="flex justify-end space-x-3">
              <button 
                type="button" 
                @click="showAddMatchModal = false"
                class="px-5 py-2.5 border border-amber-700/30 text-amber-300 rounded-lg hover:bg-amber-900/20 transition-all duration-300 flex items-center"
              >
                <i class="fas fa-times mr-2"></i> Annuler
              </button>
              <button 
                type="submit"
                class="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-5 py-2.5 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 font-medium shadow-lg shadow-amber-900/20 flex items-center"
              >
                <i class="fas fa-save mr-2"></i> Enregistrer
              </button>
            </div>

            <!-- Debug tools - Hidden button, double-click to activate -->
            <div class="mt-3 text-center opacity-20 hover:opacity-100 transition-opacity">
              <button 
                type="button" 
                @click="isDebug = !isDebug; if(isDebug) loadUsers()"
                class="text-xs text-amber-700 hover:text-amber-500 flex items-center justify-center mx-auto"
              >
                <i class="fas" :class="isDebug ? 'fa-bug' : 'fa-cog'"></i>
                <span class="ml-1">{{ isDebug ? 'Mode Debug Activé' : '⚙' }}</span>
              </button>
              <div v-if="isDebug" class="mt-2 text-xs text-left bg-black/80 p-3 rounded border border-amber-900/50">
                <p>Utilisateurs chargés: {{ players.length }}</p>
                <p>État du chargement: {{ store.getters['user/isLoading'] ? 'En cours' : 'Terminé' }}</p>
                <p>Erreur: {{ hasLoadError ? loadErrorMessage : 'Aucune' }}</p>
                <button 
                  @click="loadUsersDirectly" 
                  class="mt-2 px-2 py-1 bg-amber-900/50 text-amber-300 rounded text-xs flex items-center"
                >
                  <i class="fas fa-database mr-1"></i> Charger API directe
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal de confirmation de suppression -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 w-full max-w-md border border-amber-700/30 shadow-2xl shadow-amber-900/20 animate-fadeIn">
          <h2 class="text-xl font-bold mb-4 text-amber-300 flex items-center">
            <i class="fas fa-exclamation-triangle mr-2 text-red-400"></i>
            Confirmer la suppression
          </h2>
          <p class="text-amber-200/80 mb-6">
            Êtes-vous sûr de vouloir supprimer ce match entre <span class="font-medium text-amber-300">{{ matchToDelete ? getPlayerName(matchToDelete.player1) : '' }}</span> et <span class="font-medium text-amber-300">{{ matchToDelete ? getPlayerName(matchToDelete.player2) : '' }}</span> ?
          </p>
          <div class="flex justify-end space-x-3">
            <button 
              type="button" 
              @click="showDeleteModal = false"
              class="px-5 py-2.5 border border-amber-700/30 text-amber-300 rounded-lg hover:bg-amber-900/20 transition-all duration-300 flex items-center"
            >
              <i class="fas fa-times mr-2"></i> Annuler
            </button>
            <button 
              type="button"
              @click="deleteMatch"
              class="bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-2.5 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 font-medium shadow-lg shadow-red-900/20 flex items-center"
            >
              <i class="fas fa-trash-alt mr-2"></i> Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Liste des matchs -->
      <div class="bg-gradient-to-br from-black/95 to-amber-950/30 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border-2 border-amber-700/50 relative box-glow hover:border-amber-600/70 transition-all duration-300">
        <div class="absolute inset-0 opacity-5 bg-amber-300 pattern-warhammer"></div>
        <div class="px-6 py-6 border-b border-amber-800/60 bg-gradient-to-r from-amber-900/60 via-black/90 to-amber-900/60 relative">
          <h2 class="text-xl font-bold text-amber-300 flex items-center relative">
            <i class="fas fa-history mr-2"></i> Historique des matchs
          </h2>
        </div>
        <div class="overflow-x-auto relative p-2">
          <table class="min-w-full divide-y divide-amber-800/60 border-collapse">
            <thead class="bg-gradient-to-r from-amber-950/80 via-black/95 to-amber-950/80">
              <tr>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-calendar-alt mr-2"></i> Date
                  </div>
                </th>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-user mr-2"></i> Joueur 1
                  </div>
                </th>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-user mr-2"></i> Joueur 2
                  </div>
                </th>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-trophy mr-2"></i> Vainqueur
                  </div>
                </th>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-flag mr-2"></i> Type
                  </div>
                </th>
                <th class="px-6 py-5 text-left text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center">
                    <i class="fas fa-info-circle mr-2"></i> État
                  </div>
                </th>
                <th v-show="isAdmin" class="px-6 py-5 text-center text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  <div class="flex items-center justify-center">
                    <i class="fas fa-cogs mr-2"></i> Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(match, index) in matches" :key="match.id" :class="[
                index % 2 === 0 ? 'bg-black/80' : 'bg-amber-950/40',
                'hover:bg-amber-900/40 transition-colors duration-200 transform hover:scale-101'
              ]">
                <td class="px-6 py-6 whitespace-nowrap text-sm font-medium text-amber-200 border-b border-amber-900/30">
                  <div class="flex items-center">
                    <i class="fas fa-calendar-day mr-2 text-amber-400/70"></i>
                    <div>
                      <div class="font-semibold">{{ formatDate(match.date, match.time) }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6 whitespace-nowrap border-b border-amber-900/30">
                  <div class="flex flex-col">
                    <div class="text-sm font-semibold text-amber-200 mb-2.5 flex items-center">
                      <i class="fas fa-user-circle mr-2 text-amber-400/70"></i>
                      {{ match.user1 ? match.user1.username : getPlayerName(match.player1) }}
                    </div>
                    <div :class="['text-xs px-3 py-1.5 rounded-md border-2 shadow-inner inline-flex items-center max-w-fit font-medium', getFactionColor(getArmyFaction(match.armee1 || match.army1))]">
                      <span class="h-3 w-3 rounded-full mr-2 shadow-sm" :class="getFactionDot(getArmyFaction(match.armee1 || match.army1))"></span>
                      {{ match.armee1 || getArmyName(match.army1) }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6 whitespace-nowrap border-b border-amber-900/30">
                  <div class="flex flex-col">
                    <div class="text-sm font-semibold text-amber-200 mb-2.5 flex items-center">
                      <i class="fas fa-user-circle mr-2 text-amber-400/70"></i>
                      {{ match.user2 ? match.user2.username : getPlayerName(match.player2) }}
                    </div>
                    <div :class="['text-xs px-3 py-1.5 rounded-md border-2 shadow-inner inline-flex items-center max-w-fit font-medium', getFactionColor(getArmyFaction(match.armee2 || match.army2))]">
                      <span class="h-3 w-3 rounded-full mr-2 shadow-sm" :class="getFactionDot(getArmyFaction(match.armee2 || match.army2))"></span>
                      {{ match.armee2 || getArmyName(match.army2) }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6 whitespace-nowrap border-b border-amber-900/30">
                  <div class="inline-flex items-center">
                    <i class="fas fa-crown text-yellow-400 mr-2"></i>
                    <div class="text-sm font-bold text-amber-300">
                      {{ match.scoreUser1 > match.scoreUser2 
                        ? (match.user1 ? match.user1.username : getPlayerName(match.player1)) 
                        : (match.user2 ? match.user2.username : getPlayerName(match.player2)) }}
                    </div>
                  </div>
                </td>
                <td class="px-6 py-6 whitespace-nowrap border-b border-amber-900/30">
                  <div class="inline-flex items-center">
                    <i class="fas fa-flag mr-2" :class="getMatchTypeIcon(match.matchType)"></i>
                    <span class="text-sm font-medium px-3 py-1 rounded-full border-2" :class="getMatchTypeStyle(match.matchType)">
                      {{ getMatchTypeLabel(match.matchType) }}
                    </span>
                  </div>
                </td>
                <td class="px-6 py-6 whitespace-nowrap border-b border-amber-900/30">
                  <div v-if="match.statut !== 'VALIDE'" class="inline-flex items-center">
                    <i class="fas fa-clock text-amber-400 mr-2"></i>
                    <span class="text-sm font-medium text-amber-300 bg-amber-900/30 px-3 py-1 rounded-full border border-amber-700/50">
                      En attente
                    </span>
                  </div>
                  <div v-else class="inline-flex items-center">
                    <i class="fas fa-check-circle text-green-400 mr-2"></i>
                    <span class="text-sm font-medium text-green-300 bg-green-900/30 px-3 py-1 rounded-full border border-green-700/50">
                      Validé
                    </span>
                  </div>
                </td>
                <td v-show="isAdmin" class="px-6 py-6 whitespace-nowrap text-center border-b border-amber-900/30">
                  <div class="flex justify-center gap-4">
                    <button 
                      v-if="match.statut !== 'VALIDE'"
                      @click="validateMatch(match.id)" 
                      class="text-green-400 hover:text-green-300 transition-all duration-200 focus:outline-none p-2.5 hover:bg-gradient-to-br hover:from-green-900/40 hover:to-black/80 rounded-lg border-2 border-green-700/40 hover:border-green-600/70 shadow-md" 
                      title="Valider ce match"
                    >
                      <i class="fas fa-check"></i>
                    </button>
                    <button 
                      v-if="match.statut !== 'VALIDE'"
                      @click="editMatch(match)" 
                      class="text-amber-400 hover:text-amber-300 transition-all duration-200 focus:outline-none p-2.5 hover:bg-gradient-to-br hover:from-amber-900/40 hover:to-black/80 rounded-lg border-2 border-amber-700/40 hover:border-amber-600/70 shadow-md" 
                      title="Modifier ce match"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      v-if="match.statut !== 'VALIDE'"
                      @click="confirmDeleteMatch(match)" 
                      class="text-red-400 hover:text-red-300 transition-all duration-200 focus:outline-none p-2.5 hover:bg-gradient-to-br hover:from-red-900/40 hover:to-black/80 rounded-lg border-2 border-red-700/40 hover:border-red-600/70 shadow-md"
                      title="Supprimer ce match"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="matches.length === 0">
                <td colspan="7" class="px-6 py-12 text-center text-amber-300/50 bg-black/80 flex items-center justify-center">
                  <i class="fas fa-info-circle mr-2"></i> Aucun match enregistré
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'

const store = useStore()

const showAddMatchModal = ref(false)
const showDeleteModal = ref(false)
const isEditing = ref(false)
const matchToDelete = ref(null)

// Debugging flag
const isDebug = ref(false)

// Récupérer les données depuis le store
const matches = computed(() => {
  const all = store.getters['match/allMatches'] || [];
  // Tri personnalisé : matchs non validés en premier, puis par date+heure de création décroissante
  return [...all].sort((a, b) => {
    // Si l'un est validé et l'autre non, le non validé vient en premier
    if (a.statut === 'VALIDE' && b.statut !== 'VALIDE') return 1;
    if (a.statut !== 'VALIDE' && b.statut === 'VALIDE') return -1;
    
    // Si les deux ont le même statut, trier par date+heure décroissante (plus récent en premier)
    const dateTimeA = new Date(`${a.date || '1970-01-01'}T${a.time || '00:00:00'}`);
    const dateTimeB = new Date(`${b.date || '1970-01-01'}T${b.time || '00:00:00'}`);
    return dateTimeB - dateTimeA;
  });
})
const isLoading = computed(() => store.getters['match/isLoading'])
const error = computed(() => store.getters['match/error'])

// Store pour les utilisateurs
const players = computed(() => {
  const users = store.getters['user/allUsers'];
  console.log('Users from store:', users);
  return users || [];
})

// Liste des armées disponibles dans Warhammer Age of Sigmar
const armies = ref([
  { id: 1, name: "Stormcast Eternals", faction: "Ordre" },
  { id: 2, name: "Seraphon", faction: "Ordre" },
  { id: 3, name: "Lumineth Realm-lords", faction: "Ordre" },
  { id: 4, name: "Sylvaneth", faction: "Ordre" },
  { id: 5, name: "Idoneth Deepkin", faction: "Ordre" },
  { id: 6, name: "Daughters of Khaine", faction: "Ordre" },
  { id: 7, name: "Kharadron Overlords", faction: "Ordre" },
  { id: 8, name: "Fyreslayers", faction: "Ordre" },
  { id: 9, name: "Cities of Sigmar", faction: "Ordre" },
  { id: 10, name: "Orruk Warclans", faction: "Destruction" },
  { id: 11, name: "Gloomspite Gitz", faction: "Destruction" },
  { id: 12, name: "Ogor Mawtribes", faction: "Destruction" },
  { id: 13, name: "Sons of Behemat", faction: "Destruction" },
  { id: 14, name: "Ossiarch Bonereapers", faction: "Mort" },
  { id: 15, name: "Nighthaunt", faction: "Mort" },
  { id: 16, name: "Flesh-eater Courts", faction: "Mort" },
  { id: 17, name: "Soulblight Gravelords", faction: "Mort" },
  { id: 18, name: "Skaven", faction: "Chaos" },
  { id: 19, name: "Slaves to Darkness", faction: "Chaos" },
  { id: 20, name: "Blades of Khorne", faction: "Chaos" },
  { id: 21, name: "Disciples of Tzeentch", faction: "Chaos" },
  { id: 22, name: "Maggotkin of Nurgle", faction: "Chaos" },
  { id: 23, name: "Hedonites of Slaanesh", faction: "Chaos" },
  { id: 24, name: "Beasts of Chaos", faction: "Chaos" }
])

const newMatch = ref({
  player1: 0,
  player2: 0,
  winner: 0,
  army1: 0,
  army2: 0,
  matchType: 0
})

const getPlayerName = (playerId) => {
  const player = players.value.find(p => p.id === playerId)
  return player ? player.username : 'Inconnu'
}

const getArmyName = (armyId) => {
  const army = armies.value.find(a => a.id === armyId)
  return army ? army.name : 'Inconnue'
}

const getArmyFaction = (armyIdentifier) => {
  // Si c'est un nombre, c'est un ID d'armée
  if (typeof armyIdentifier === 'number') {
    const army = armies.value.find(a => a.id === armyIdentifier);
    return army ? army.faction : '';
  }
  // Si c'est une chaîne, c'est un nom d'armée
  if (typeof armyIdentifier === 'string') {
    // Déterminer la faction basée sur le nom
    if (
      armyIdentifier.includes('Stormcast') ||
      armyIdentifier.includes('Seraphon') ||
      armyIdentifier.includes('Lumineth') ||
      armyIdentifier.includes('Sylvaneth') ||
      armyIdentifier.includes('Idoneth') ||
      armyIdentifier.includes('Daughters') ||
      armyIdentifier.includes('Kharadron') ||
      armyIdentifier.includes('Fyreslayers') ||
      armyIdentifier.includes('Cities')
    ) {
      return 'Ordre';
    }
    if (
      armyIdentifier.includes('Orruk') ||
      armyIdentifier.includes('Gloomspite') ||
      armyIdentifier.includes('Ogor') ||
      armyIdentifier.includes('Behemat')
    ) {
      return 'Destruction';
    }
    if (
      armyIdentifier.includes('Ossiarch') ||
      armyIdentifier.includes('Nighthaunt') ||
      armyIdentifier.includes('Flesh-eater') ||
      armyIdentifier.includes('Soulblight')
    ) {
      return 'Mort';
    }
    if (
      armyIdentifier.includes('Skaven') ||
      armyIdentifier.includes('Slaves') ||
      armyIdentifier.includes('Khorne') ||
      armyIdentifier.includes('Tzeentch') ||
      armyIdentifier.includes('Nurgle') ||
      armyIdentifier.includes('Slaanesh') ||
      armyIdentifier.includes('Chaos')
    ) {
      return 'Chaos';
    }
  }
  return '';
}

const getFactionColor = (faction) => {
  const colors = {
    'Ordre': 'bg-blue-900/60 text-blue-200 border-blue-500/80',
    'Chaos': 'bg-red-900/60 text-red-200 border-red-500/80',
    'Mort': 'bg-green-900/60 text-green-200 border-green-500/80',
    'Destruction': 'bg-orange-900/60 text-orange-200 border-orange-500/80'
  }
  return colors[faction] || 'bg-gray-900/60 text-gray-200 border-gray-500/80'
}

const getFactionDot = (faction) => {
  const colors = {
    'Ordre': 'bg-gradient-to-br from-blue-300 to-blue-500',
    'Chaos': 'bg-gradient-to-br from-red-300 to-red-500',
    'Mort': 'bg-gradient-to-br from-green-300 to-green-500',
    'Destruction': 'bg-gradient-to-br from-orange-300 to-orange-500'
  }
  return colors[faction] || 'bg-gradient-to-br from-gray-300 to-gray-500'
}

const getMatchTypeLabel = (matchType) => {
  const labels = {
    'LEAGUE': 'League',
    'LADDER': 'Ladder'
  }
  return labels[matchType] || 'Inconnu'
}

const getMatchTypeStyle = (matchType) => {
  const styles = {
    'LEAGUE': 'bg-blue-900/60 text-blue-200 border-blue-500/80',
    'LADDER': 'bg-purple-900/60 text-purple-200 border-purple-500/80'
  }
  return styles[matchType] || 'bg-gray-900/60 text-gray-200 border-gray-500/80'
}

const getMatchTypeIcon = (matchType) => {
  const icons = {
    'LEAGUE': 'text-blue-400',
    'LADDER': 'text-purple-400'
  }
  return icons[matchType] || 'text-gray-400'
}

const formatDate = (dateString, timeString = null) => {
  const options = { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric'
  };
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString('fr-FR', options);
  
  // Si une heure est fournie, l'ajouter et la formater en HH:MM:SS
  if (timeString) {
    // Extraire les heures, minutes et secondes (HH:MM:SS)
    const timeOnly = timeString.substring(0, 8);
    return `${formattedDate} à ${timeOnly}`;
  }
  
  return formattedDate;
}

const addMatch = async () => {
  // Validation
  if (newMatch.value.player1 === 0 || newMatch.value.player2 === 0 || newMatch.value.winner === 0 || newMatch.value.matchType === 0) {
    alert('Veuillez remplir tous les champs');
    return;
  }
  
  if (newMatch.value.player1 === newMatch.value.player2) {
    alert('Les deux joueurs ne peuvent pas être identiques');
    return;
  }
  
  if (newMatch.value.winner !== newMatch.value.player1 && newMatch.value.winner !== newMatch.value.player2) {
    alert('Le vainqueur doit être l\'un des deux joueurs');
    return;
  }
  
  if (newMatch.value.army1 === 0 || newMatch.value.army2 === 0) {
    alert('Veuillez sélectionner une armée pour chaque joueur');
    return;
  }
  
  try {
    if (isEditing.value) {
      // Mettre à jour le match existant via le store
      await store.dispatch('match/updateMatch', {
        id: newMatch.value.id,
        matchData: {
          user1Id: newMatch.value.player1,
          user2Id: newMatch.value.player2,
          scoreUser1: newMatch.value.winner === newMatch.value.player1 ? 1 : 0,
          scoreUser2: newMatch.value.winner === newMatch.value.player2 ? 1 : 0,
          armee1: getArmyName(newMatch.value.army1),
          armee2: getArmyName(newMatch.value.army2),
          date: new Date().toISOString().split('T')[0],
          matchType: newMatch.value.matchType
        }
      });
    } else {
      // Créer un nouveau match via le store
      await store.dispatch('match/createMatch', {
        user1Id: newMatch.value.player1,
        user2Id: newMatch.value.player2,
        scoreUser1: newMatch.value.winner === newMatch.value.player1 ? 1 : 0,
        scoreUser2: newMatch.value.winner === newMatch.value.player2 ? 1 : 0,
        armee1: getArmyName(newMatch.value.army1),
        armee2: getArmyName(newMatch.value.army2),
        date: new Date().toISOString().split('T')[0],
        matchType: newMatch.value.matchType
      });
    }
    
    // Fermer le modal et réinitialiser le formulaire
    showAddMatchModal.value = false;
    isEditing.value = false;
    newMatch.value = {
      player1: 0,
      player2: 0,
      winner: 0,
      army1: 0,
      army2: 0,
      matchType: 0
    };
  } catch (error) {
    console.error('Erreur lors de l\'ajout/modification du match:', error);
    alert('Une erreur s\'est produite lors de l\'enregistrement du match.');
  }
}

const editMatch = (match) => {
  // Préparer le match pour l'édition
  newMatch.value = {
    id: match.id,
    player1: match.user1?.id || match.player1,
    player2: match.user2?.id || match.player2,
    winner: match.scoreUser1 > match.scoreUser2 ? match.user1?.id || match.player1 : match.user2?.id || match.player2,
    army1: getArmyIdByName(match.armee1) || match.army1,
    army2: getArmyIdByName(match.armee2) || match.army2,
    matchType: match.matchType || 'LADDER'
  };
  isEditing.value = true;
  showAddMatchModal.value = true;
}

const getArmyIdByName = (armyName) => {
  const army = armies.value.find(a => a.name === armyName);
  return army ? army.id : 0;
}

const confirmDeleteMatch = (match) => {
  matchToDelete.value = match;
  showDeleteModal.value = true;
}

const deleteMatch = async () => {
  if (matchToDelete.value) {
    try {
      // Supprimer le match via le store
      await store.dispatch('match/deleteMatch', matchToDelete.value.id);
      showDeleteModal.value = false;
      matchToDelete.value = null;
    } catch (error) {
      console.error('Erreur lors de la suppression du match:', error);
      alert('Une erreur s\'est produite lors de la suppression du match.');
    }
  }
}

const validateMatch = async (matchId) => {
  try {
    await store.dispatch('match/validateMatch', matchId);
  } catch (error) {
    console.error('Erreur lors de la validation du match:', error);
    alert('Une erreur s\'est produite lors de la validation du match.');
  }
}

// Fonction pour afficher des logs de debug si nécessaire
const debug = (message) => {
  if (isDebug.value) {
    console.log(`[MatchesView] ${message}`);
  }
}

// Gestion des erreurs de chargement
const hasLoadError = ref(false);
const loadErrorMessage = ref('');

// Fonction pour charger les utilisateurs directement depuis l'API
const loadUsersDirectly = async () => {
  try {
    debug('Tentative de chargement direct des utilisateurs depuis l\'API...');
    const response = await axios.get('http://localhost:8082/api/users', { 
      headers: { 'Content-Type': 'application/json' } 
    });
    
    if (response.data && Array.isArray(response.data)) {
      debug(`Chargement réussi, ${response.data.length} utilisateurs trouvés`);
      // Mettre à jour le store avec les données récupérées
      store.commit('user/setUsers', response.data);
      hasLoadError.value = false;
      return true;
    } else {
      debug('La réponse API ne contient pas de tableau d\'utilisateurs');
      hasLoadError.value = true;
      loadErrorMessage.value = 'Format de données incorrect';
      return false;
    }
  } catch (error) {
    console.error('Erreur lors du chargement direct des utilisateurs:', error);
    hasLoadError.value = true;
    loadErrorMessage.value = error.message || 'Erreur de connexion à l\'API';
    return false;
  }
};

// Fonction de chargement des utilisateurs avec retry
const loadUsers = async () => {
  // D'abord essayer via le store
  try {
    debug('Tentative de chargement des utilisateurs via le store...');
    await store.dispatch('user/fetchAllUsers');
    
    if (players.value.length > 0) {
      debug(`Chargement réussi via le store, ${players.value.length} utilisateurs trouvés`);
      hasLoadError.value = false;
      return;
    }
    
    // Si aucun utilisateur n'est chargé, essayer directement via l'API
    debug('Aucun utilisateur chargé via le store, tentative directe...');
    await loadUsersDirectly();
    
  } catch (error) {
    console.error('Erreur lors du chargement des utilisateurs:', error);
    // Essayer directement via l'API en cas d'erreur
    await loadUsersDirectly();
  }
};

// Surveiller les changements de players pour le debug
watch(players, (newVal) => {
  debug(`Liste de joueurs mise à jour: ${newVal?.length || 0} joueurs`);
}, { deep: true });

onMounted(async () => {
  try {
    debug('Montage du composant, chargement des données...');
    
    // Charger les utilisateurs d'abord
    await loadUsers();
    
    // Puis charger les matchs
    await store.dispatch('match/fetchAllMatches');
    debug(`Matchs chargés: ${matches.value?.length || 0}`);
    
    // Si aucun utilisateur n'est chargé après le premier essai, réessayer une fois
    if (players.value.length === 0) {
      debug('Aucun utilisateur chargé, nouvelle tentative...');
      setTimeout(() => {
        loadUsers();
      }, 1000);
    }
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error);
    hasLoadError.value = true;
    loadErrorMessage.value = error.message || 'Erreur de chargement';
  }
})

const isAdmin = computed(() => store.getters['auth/isAdmin'])
</script>

<style scoped>
.pattern-warhammer {
  background-image: 
    url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 0L0 12l12 12 12-12z' fill='%23D4AF37' fill-opacity='0.2'/%3E%3C/svg%3E"),
    url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0l20 20-20 20L0 20z' fill='%23D4AF37' fill-opacity='0.1'/%3E%3C/svg%3E");
  background-size: 30px 30px, 60px 60px;
}

@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}

.box-glow {
  box-shadow: 0 0 30px rgba(217, 119, 6, 0.15), 
              0 5px 20px rgba(0, 0, 0, 0.3), 
              inset 0 0 15px rgba(217, 119, 6, 0.05);
  transition: box-shadow 0.3s ease-in-out;
}

.box-glow:hover {
  box-shadow: 0 0 35px rgba(217, 119, 6, 0.25), 
              0 5px 20px rgba(0, 0, 0, 0.4), 
              inset 0 0 20px rgba(217, 119, 6, 0.1);
}

.hover\:scale-101:hover {
  transform: scale(1.01);
}

.shadow-glow {
  box-shadow: 0 0 10px rgba(217, 119, 6, 0.3);
}

.animate-pulse-slow {
  animation: pulse 3s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}
</style>